/**
 * ============================================================================
 * ⭐ DRAW ONE PAGE OF A PDF — for the load-received stamp (F120, Decision 302)
 * ============================================================================
 *
 * > Pete, 2026-09-29: *"Can we fix this to show the document behind it so we know
 * > where to place the signature?"* → popup *"Exact page render"*.
 *
 * ⭐ LOADED ONLY WHEN IT IS USED. `pdfjs-dist` is imported dynamically, so the
 * library and its worker are fetched the first time a stamp window opens — never
 * on the board. The worker is a file of this site's own build (Vite `?url`), not
 * a CDN, so nothing here reaches another host (⛔ nothing shipped points anywhere
 * but us).
 *
 * ⛔⛔ THE SAME PAGE THE SERVER STAMPS. `documentSigning.ts` places the stamp on
 * the VISIBLE page: the MediaBox turned by /Rotate (`visibleSize`). pdf.js's
 * default viewport is that page turned by the same /Rotate, so the picture here
 * is the page the fractions are measured against. ⛔ NEVER pass `rotation` to
 * `getViewport` — overriding it would draw a page the server does not stamp.
 * ⚠️ Known limit: pdf.js draws the CropBox and the server measures the MediaBox.
 * Scanners write only a MediaBox, so they are the same; a PDF with a smaller
 * CropBox would place the stamp slightly off, and the signed copy shows it.
 *
 * ⚠️ `scale: 1` is one PDF point per unit, so `pageSize` is in points — the unit
 * the stamp's font size is in. The dialog scales the preview stamp with it.
 */
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

export interface PdfHandle {
    pageCount: number;
    /** The visible page, in PDF points (rotation applied). */
    pageSize(page: number): Promise<{ width: number; height: number }>;
    /** Draw `page` into `canvas`, `cssWidth` CSS pixels wide. Resolves when drawn. */
    render(page: number, canvas: HTMLCanvasElement, cssWidth: number): Promise<void>;
    /** Stop any draw in progress. */
    cancel(): void;
    destroy(): void;
}

const clampPage = (doc: PDFDocumentProxy, page: number) =>
    Math.min(Math.max(1, Math.round(page) || 1), doc.numPages);

/** Open a PDF from its bytes. Throws when the bytes are not a readable PDF. */
export async function openPdf(data: ArrayBuffer): Promise<PdfHandle> {
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
    const doc = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
    let task: RenderTask | null = null;

    return {
        pageCount: doc.numPages,
        async pageSize(page) {
            const p = await doc.getPage(clampPage(doc, page));
            const v = p.getViewport({ scale: 1 });
            return { width: v.width, height: v.height };
        },
        async render(page, canvas, cssWidth) {
            task?.cancel();
            const p = await doc.getPage(clampPage(doc, page));
            const base = p.getViewport({ scale: 1 });
            const ratio = window.devicePixelRatio || 1;
            const viewport = p.getViewport({ scale: (cssWidth * ratio) / base.width });
            canvas.width = Math.max(1, Math.floor(viewport.width));
            canvas.height = Math.max(1, Math.floor(viewport.height));
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('This browser cannot draw the page.');
            task = p.render({ canvasContext: ctx, viewport });
            try {
                await task.promise;
            } finally {
                task = null;
            }
        },
        cancel() {
            task?.cancel();
            task = null;
        },
        destroy() {
            task?.cancel();
            task = null;
            void doc.destroy();
        },
    };
}

/** A cancelled draw is not a failure — the page changed or the window closed. */
export function isCancelledRender(err: unknown): boolean {
    return !!err && typeof err === 'object' && (err as { name?: string }).name === 'RenderingCancelledException';
}
